import { useContext } from 'react';
import { graphql } from 'react-relay';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryNoFurtherUpdateInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryNoFurtherUpdateInterop.ts';

import { SpaceDetailContext } from './SpaceDetailContext.ts';
import type {
	useSpaceDetailQuery$data,
	useSpaceDetailQuery$variables,
} from './__generated__/useSpaceDetailQuery.graphql.ts';

export type SpaceDetailDataType = {
	spaceId: string;
	type: string;
	name: string;
	alias?: string;
	dataClassificationTags: ReadonlyArray<string | null | undefined>;
	homepage?: {
		id: string;
		title: string;
		type: string;
	};
	iconPath?: string;
	typeSettings?: {
		enabledContentTypes?: {
			isLivePagesEnabled: boolean;
			isWhiteboardsEnabled: boolean;
			isDatabasesEnabled: boolean;
			isSlidesEnabled: boolean;
		};
	};
};

interface CommonHookResult {
	loading: boolean;
	error?: ApolloErrorInterop;
}

export interface SpaceDetailType extends CommonHookResult {
	data?: SpaceDetailDataType;
}

export const useSpaceDetail = (
	spaceKey: string | null | undefined,
	{
		includeSpaceTypeSettings = false,
		skip = false,
	}: { includeSpaceTypeSettings?: boolean; skip?: boolean } = {},
): SpaceDetailType => {
	/** @schema cc */
	const useSpaceDetailQuery = graphql`
		query useSpaceDetailQuery($spaceKey: String, $includeSpaceTypeSettings: Boolean!) {
			space(key: $spaceKey) {
				id
				name
				alias
				key
				type
				dataClassificationTags
				homepage {
					id
					title
					type
				}
				icon {
					path
				}
				spaceTypeSettings @include(if: $includeSpaceTypeSettings) {
					enabledContentTypes {
						isLivePagesEnabled
						isWhiteboardsEnabled
						isDatabasesEnabled
						isSlidesEnabled
					}
				}
			}
		}
	`;
	const sharedDetail = useContext(SpaceDetailContext);
	const canUseSharedDetail =
		Boolean(spaceKey) &&
		sharedDetail?.spaceKey === spaceKey &&
		(!includeSpaceTypeSettings || sharedDetail?.space?.spaceTypeSettings !== undefined);
	const options = {
		variables: { spaceKey, includeSpaceTypeSettings },
		...(skip || canUseSharedDetail ? { skip: true } : {}),
	};

	const legacyResult = useQueryNoFurtherUpdateInterop<
		useSpaceDetailQuery$data,
		useSpaceDetailQuery$variables
	>(useSpaceDetailQuery, options);

	if (skip) {
		return { loading: false };
	}

	const { loading, data, error } =
		canUseSharedDetail && sharedDetail
			? {
					loading: sharedDetail.loading,
					data: sharedDetail.space ? { space: sharedDetail.space } : undefined,
					error: undefined,
				}
			: legacyResult;

	return {
		loading,
		data: data
			? ({
					spaceId: data.space?.id,
					type: data.space?.type,
					name: data.space?.name,
					dataClassificationTags: data.space?.dataClassificationTags,
					alias: data.space?.alias,
					homepage: data.space?.homepage,
					iconPath: data.space?.icon?.path,
					typeSettings:
						includeSpaceTypeSettings && data.space?.spaceTypeSettings
							? {
									enabledContentTypes: {
										...data.space.spaceTypeSettings.enabledContentTypes,
									},
								}
							: undefined,
				} as any)
			: undefined,
		error,
	};
};
